import React, { useState, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/native';
import moment from 'moment';
import Text from '../text';
import Colors from '../colors';
import Spacer from '../spacer';
import useMobile from '../hooks/use-mobile';
import Icon from '../icon';
import LabelDataPair from '../label-data-pair';
import BreakdownRow from '../breakdown-row';
import Status from '../status';
import Currency from '../utilities/currency';
const DPD_GRACE_PERIOD = 30;
const StyledContainer = styled.View`
  flex-direction: column;
  justify-content: space-between;
  background-color: ${Colors.white};
  border: ${props => props.expanded ? 1 : 0}px solid ${Colors.lightGray500};
  border-radius: 4px;
  width: 100%;
  filter: drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.1));
`;
const StyledContentRow = styled.View`
  flex-direction: row;
  align-items: center;
`;
const StyledPressable = styled.Pressable`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-top: ${props => props.padding}px;
  padding-left: 14px;
  padding-bottom: ${props => props.expanded ? 0 : props.padding}px
  padding-right: 16px;
`;
const BreakdownContainer = styled.View`
  padding: 12px 16px;
`;
const BreakdownContent = styled.View`
  padding: 16px;
  background-color: ${Colors.lightGray100};
`;
const WarningIconContainer = styled.View`
  flex-direction: row;
  margin-left: auto;
  margin-top: 12px;
`;

const formatDate = date => moment(date).format('MMM DD, YYYY');

const devices = {
  desktop: {
    containerPadding: 16,
    dueCaretSpacer: 'f20'
  },
  mobile: {
    containerPadding: 12,
    dueCaretSpacer: 'f16'
  }
};
export default function Breakdown(props) {
  const mobile = useMobile();
  const ref = useRef();
  const [expanded, setExpanded] = useState(false);
  const device = mobile ? devices.mobile : devices.desktop;
  return /*#__PURE__*/React.createElement(StyledContainer, {
    expanded: expanded
  }, /*#__PURE__*/React.createElement(StyledPressable, {
    padding: device.containerPadding,
    expanded: expanded,
    ref: ref,
    onPress: () => setExpanded(!expanded)
  }, /*#__PURE__*/React.createElement(StyledContentRow, null, /*#__PURE__*/React.createElement(LabelDataPair, {
    type: "bottom-left",
    label: `Payment ${props.paymentNo} of ${props.paymentCount}`
  }, Currency.pesoFormat(props.total))), props.warningIcon && /*#__PURE__*/React.createElement(WarningIconContainer, null, /*#__PURE__*/React.createElement(Icon.MaterialIcons, {
    name: "warning",
    size: 20,
    color: Colors.warning400
  }), /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    width: true
  })), /*#__PURE__*/React.createElement(StyledContentRow, null, props.paid && /*#__PURE__*/React.createElement(Status, {
    type: "success"
  }, "Paid") || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LabelDataPair, {
    type: "bottom-right",
    label: "Due On"
  }, formatDate(props.date)), /*#__PURE__*/React.createElement(Spacer, {
    size: device.dueCaretSpacer,
    width: true
  })), /*#__PURE__*/React.createElement(Icon.MaterialIcons, {
    name: expanded ? 'expand-less' : 'expand-more',
    size: 24,
    color: Colors.gray600
  }))), expanded && /*#__PURE__*/React.createElement(BreakdownContainer, null, props.children, /*#__PURE__*/React.createElement(BreakdownContent, null, /*#__PURE__*/React.createElement(BreakdownRow, {
    type: "default",
    label: "Amount Advanced",
    amount: props.amount
  }), /*#__PURE__*/React.createElement(Spacer, {
    size: "f12",
    height: true
  }), /*#__PURE__*/React.createElement(BreakdownRow, {
    type: "fees",
    label: "Fees",
    amount: props.fees,
    tooltip: props.tooltip
  }), (props.dpd > DPD_GRACE_PERIOD || props.penalties > 0) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacer, {
    size: "f12",
    height: true
  }), /*#__PURE__*/React.createElement(BreakdownRow, {
    type: "default",
    label: "Penalties",
    amount: props.penalties
  })), props.discounts > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacer, {
    size: "f12",
    height: true
  }), /*#__PURE__*/React.createElement(BreakdownRow, {
    type: "discount",
    label: "Discounts",
    amount: props.discounts
  })), /*#__PURE__*/React.createElement(Spacer, {
    size: "f12",
    height: true
  }), /*#__PURE__*/React.createElement(BreakdownRow, {
    type: "total",
    label: "Total Amount Due",
    amount: props.total
  }), props.paid && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacer, {
    size: "f12",
    height: true
  }), /*#__PURE__*/React.createElement(BreakdownRow, {
    type: "default",
    label: "Payment Completed On"
  }, /*#__PURE__*/React.createElement(Text.BodySmall, {
    color: Colors.gray400
  }, moment(props.date).format('MMM DD, YYYY')))))));
}
Breakdown.propTypes = {
  date: PropTypes.instanceOf(Date),
  paid: PropTypes.bool,
  paymentNo: PropTypes.number,
  paymentCount: PropTypes.number,
  amount: PropTypes.number,
  fees: PropTypes.number,
  penalties: PropTypes.number,
  discounts: PropTypes.number,
  total: PropTypes.number,
  dpd: PropTypes.number,
  warningIcon: PropTypes.bool,
  children: PropTypes.node,
  tooltip: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    contentMaxWidth: PropTypes.number,
    textWidth: PropTypes.number
  })
};
Breakdown.defaultProps = {
  date: null,
  paid: false,
  paymentNo: 0,
  paymentCount: 0,
  amount: 0,
  fees: 0,
  penalties: 0,
  discounts: 0,
  total: 0,
  dpd: 0,
  warningIcon: false,
  children: null,
  tooltip: {
    title: '',
    content: '',
    contentMaxWidth: 200,
    textWidth: 136
  }
};
//# sourceMappingURL=breakdown.js.map